import React, { useMemo } from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import { DialogBox } from '@mummssoftware/common-ui';
import type { PatientChatPatientSummary } from '@mummssoftware/utils';

import { MaiaChatAgentContext, useMaiaChatAgentContext } from '../../context';
import defaults from '../../translations/en.json';
import type { QuestionDateFilterState } from './QuestionDateFilter';

const BackButton = styled(Button)`
  && {
    margin: 6px 0 0 8px;
    text-transform: none;
  }
`;

type PatientChatsDialogProps = {
  /** The patient row clicked in MAIA Chats; null keeps the dialog closed. */
  row: PatientChatPatientSummary | null;
  /** The list's date filter; the patient's history shows and sets the same one. */
  questionDateFilter?: QuestionDateFilterState | null;
  onBack: () => void;
  /** The patient's MAIA widget, rendered under that patient's context. */
  children: React.ReactNode;
};

export const patientDialogTitle = (row: PatientChatPatientSummary) =>
  row.patientName
    ? `${row.patientName} - ${row.patientNumber}`
    : row.patientNumber;

/**
 * MAIA Chats drill-in: the clicked patient's MAIA widget in a dialog over the
 * list (like HOPE's detail dialog), the same as on the patient dashboard: its
 * history, the date filter and + to start a chat. The date filter is the
 * list's own: new dates picked here reload the list underneath too. The list
 * stays mounted, so its sort and column filters are as the user left them on
 * Back.
 */
export const PatientChatsDialog = ({
  row,
  questionDateFilter,
  onBack,
  children,
}: PatientChatsDialogProps) => {
  const context = useMaiaChatAgentContext();
  const patientContext = useMemo(
    () => ({
      ...context,
      patientNumber: row?.patientNumber,
      // Not loaded here: no start-date picker, so a chat covers the last 365 days.
      patient: undefined,
      questionDateFilter,
    }),
    [context, row, questionDateFilter],
  );

  if (!row) {
    return null;
  }

  return (
    <DialogBox
      open
      handleClick={onBack}
      title={patientDialogTitle(row)}
      fullWidth
      maxWidth="lg"
      disableScrollLock
      // Back to list is the only way out of the header; no Done checkmark.
      noIcon
      contentStyle={{ padding: 0 }}
      TopButton={
        <BackButton
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          data-testid="maia-back-to-list"
        >
          {defaults.backToList}
        </BackButton>
      }
    >
      <MaiaChatAgentContext.Provider value={patientContext}>
        {children}
      </MaiaChatAgentContext.Provider>
    </DialogBox>
  );
};

export default PatientChatsDialog;
